import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Select, Tag, Modal, Input, message } from "antd";
import Table from "../../../../core/common/dataTable/index";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactDatePicker from "react-datepicker";
import {
  faShieldHalved,
  faHouse,
  faLaptopHouse,
  faPersonWalkingLuggage,
  faSuitcase,
  faDollarSign,
  faCalendarCheck,
  faFileText,
} from "@fortawesome/free-solid-svg-icons";
import { useAuthToken } from "../../../../core/data/redux/useAuthToken";
import {
  getAllPermissions,
  PermissionRecord,
  PermissionWidgets,
  updatePermissionStatus,
  getPermissionWidgets,
} from "../../../../core/api/PermissionReq";
import useGetEmployees from "../../../../core/api/useGetEmployees";
import { API_BASE_PATH } from "../../../../environment";
import dayjs from "dayjs";
import {
  getAllWorkFromHome,
  getWorkFromHomeWidgets,
  updateWorkFromHomeStatus,
  WorkFromHomeRecord,
  WorkFromHomeWidgets,
} from "../../../../core/api/useWFH";
import {
  getAllOnDuties,
  OnDutyRecord,
  OnDutyWidgets,
  getOnDutyWidgets,
  updateOnDutyStatus,
} from "../../../../core/api/OnDuty";
import { toast } from "react-toastify";
import {
  ApplyLeaveRecord,
  getApplyLeaveWidgets,
  getAllApplyLeave,
  putApplyLeave,
} from "../../../../core/api/applyLeave";
import {
  getAllLeaveTypes,
  getLeaveBalanceByEmployee,
  LeaveBalanceRecord,
  LeaveTypeRecord,
} from "../../../../core/api/leaveType";
import { fetchEmployeeImage } from "../../../../core/api/fetchEmployeeImage";

type TableRecord =
  | PermissionRecord
  | OnDutyRecord
  | WorkFromHomeRecord
  | ApplyLeaveRecord;

const LeaveDashboard = () => {
  const { Option } = Select;
  const BASE_PATH = API_BASE_PATH;
  const [viewType, setViewType] = useState<string>("default");
  const [tableData, setTableData] = useState<TableRecord[]>([]);
  const [originalData, setOriginalData] = useState<TableRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPendingView, setIsPendingView] = useState<boolean>(false); // Track if view is from Pending Requests
  const { employees, isLoading: loadingEmployees } = useGetEmployees();
  const empToken = useAuthToken();
  const tableRef = useRef<HTMLDivElement | null>(null); // For scroll
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [viewMode, setViewMode] = useState<"date" | "month">("month");
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeRecord[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceRecord[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [empImages, setEmpImages] = useState<Record<string, string>>({}); // State for employee images
  const toggleMonthDropdown = () => setIsMonthDropdownOpen((prev) => !prev);

  const [selectedStatuses, setSelectedStatuses] = useState<
    Record<string | number, string | undefined>
  >({});

  const [leaveStats, setLeaveStats] = useState({
    applied: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
  });

  const [leaveTypeData, setLeaveTypeData] = useState<{
    labels: string[];
    values: number[];
  } | null>(null);

  const [permissionStats, setPermissionStats] = useState<PermissionWidgets>({
    applied: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
  });

  const [onDutyStats, setOnDutyStats] = useState<OnDutyWidgets>({
    applied: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
  });

  const [wfhStats, setWfhStats] = useState<WorkFromHomeWidgets>({
    applied: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
  });

  const leaveTypeMap = useMemo(() => {
    const map: Record<number, string> = {};
    leaveTypes.forEach((type) => {
      map[type.leave_type_id] = type.leave_type_name || "Unknown Leave Type";
    });
    return map;
  }, [leaveTypes]);

  const refreshData = async () => {
    try {
      setLoading(true);

      if (employees.length > 0) {
        const [types, balances] = await Promise.all([
          getAllLeaveTypes(empToken),
          getLeaveBalanceByEmployee(
            employees.find((emp) => emp.employee_id)?.employee_id || "",
            empToken
          ),
        ]);
        setLeaveTypes(types);
        setLeaveBalances(balances);
      }

      const monthToUse =
        viewMode === "date" && selectedDate
          ? selectedDate.month() + 1
          : selectedMonth + 1;
      const yearToUse =
        viewMode === "date" && selectedDate
          ? selectedDate.year()
          : selectedYear;
      const dateToUse =
        viewMode === "date" && selectedDate ? selectedDate.date() : null;

      const [leaveData, permissionData, onDutyData, wfhData] =
        await Promise.all([
          getApplyLeaveWidgets(monthToUse, yearToUse, empToken, dateToUse),
          getPermissionWidgets(
            monthToUse,
            yearToUse,
            dateToUse,
            null,
            empToken
          ),
          getOnDutyWidgets(monthToUse, yearToUse, dateToUse, null, empToken),
          getWorkFromHomeWidgets(
            monthToUse,
            yearToUse,
            dateToUse,
            null,
            empToken
          ),
        ]);

      setLeaveStats({
        applied: leaveData.applied,
        approved: leaveData.approved,
        rejected: leaveData.rejected,
        pending: leaveData.pending,
      });

      if (leaveData.leave_count_name) {
        setLeaveTypeData({
          labels: Object.keys(leaveData.leave_count_name || {}),
          values: Object.values(leaveData.leave_count_name || {}),
        });
      }

      setPermissionStats({
        applied: permissionData.applied,
        approved: permissionData.approved,
        rejected: permissionData.rejected,
        pending: permissionData.pending,
      });

      setOnDutyStats({
        applied: onDutyData.applied,
        approved: onDutyData.approved,
        rejected: onDutyData.rejected,
        pending: onDutyData.pending,
      });

      setWfhStats({
        applied: wfhData.applied,
        approved: wfhData.approved,
        rejected: wfhData.rejected,
        pending: wfhData.pending,
      });

      if (viewType !== "default") {
        let data: TableRecord[] = [];
        if (viewType === "permission") data = await getAllPermissions(empToken);
        else if (viewType === "od") data = await getAllOnDuties(empToken);
        else if (viewType === "wfh") data = await getAllWorkFromHome(empToken);
        else if (viewType === "leave") data = await getAllApplyLeave(empToken);
        setOriginalData(data);
        setTableData(
          isPendingView
            ? data.filter(
                (record) =>
                  record.status === "Pending" &&
                  getRecordType(record) === viewType
              )
            : data
        );
      }
    } catch (error) {
      console.error("Failed to refresh data", error);
      setTableData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empToken && employees.length > 0) {
      refreshData();
    }
  }, [
    empToken,
    employees,
    viewType,
    selectedMonth,
    selectedYear,
    selectedDate,
    viewMode,
  ]);

  useEffect(() => {
    const fetchLeaveTypesAndBalances = async () => {
      try {
        const [types, balances] = await Promise.all([
          getAllLeaveTypes(empToken),
          getLeaveBalanceByEmployee(
            employees.find((emp) => emp.employee_id)?.employee_id || "",
            empToken
          ),
        ]);
        setLeaveTypes(types);
        setLeaveBalances(balances);
      } catch (error) {
        console.error("Failed to fetch leave types and balances", error);
      }
    };

    if (empToken && employees.length > 0) {
      fetchLeaveTypesAndBalances();
    }
  }, [empToken, employees]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!empToken) return;

        const monthToUse =
          viewMode === "date" && selectedDate
            ? selectedDate.month() + 1
            : selectedMonth + 1;
        const yearToUse =
          viewMode === "date" && selectedDate
            ? selectedDate.year()
            : selectedYear;
        const dateToUse =
          viewMode === "date" && selectedDate ? selectedDate.date() : null;

        const leaveData = await getApplyLeaveWidgets(
          monthToUse,
          yearToUse,
          empToken,
          dateToUse
        );
        setLeaveStats({
          applied: leaveData.applied,
          approved: leaveData.approved,
          rejected: leaveData.rejected,
          pending: leaveData.pending,
        });

        if (leaveData.leave_count_name) {
          setLeaveTypeData({
            labels: Object.keys(leaveData.leave_count_name || {}),
            values: Object.values(leaveData.leave_count_name || {}),
          });
        }

        const permissionData = await getPermissionWidgets(
          monthToUse,
          yearToUse,
          dateToUse,
          null,
          empToken
        );
        setPermissionStats({
          applied: permissionData.applied,
          approved: permissionData.approved,
          rejected: permissionData.rejected,
          pending: permissionData.pending,
        });

        const onDutyData = await getOnDutyWidgets(
          monthToUse,
          yearToUse,
          dateToUse,
          null,
          empToken
        );
        setOnDutyStats({
          applied: onDutyData.applied,
          approved: onDutyData.approved,
          rejected: onDutyData.rejected,
          pending: onDutyData.pending,
        });

        const wfhData = await getWorkFromHomeWidgets(
          monthToUse,
          yearToUse,
          dateToUse,
          null,
          empToken
        );
        setWfhStats({
          applied: wfhData.applied,
          approved: wfhData.approved,
          rejected: wfhData.rejected,
          pending: wfhData.pending,
        });
      } catch (err) {
        console.error("Failed to fetch stats", err);
      }
    };

    fetchStats();
  }, [empToken, selectedMonth, selectedYear, selectedDate, viewMode]);

  useEffect(() => {
    const loadImages = async () => {
      const uniqueEmployeeIds = new Set(
        tableData.map((record) => record.employee_id).filter(Boolean)
      );
      for (const empId of Array.from(uniqueEmployeeIds)) {
        // Convert Set to Array
        if (!empImages[empId] && empToken) {
          const imgUrl = await fetchEmployeeImage(empId, empToken);
          setEmpImages((prev) => ({ ...prev, [empId]: imgUrl }));
        }
      }
    };
    if (tableData.length > 0 && empToken) {
      loadImages();
    }
  }, [tableData, empToken]);

  useEffect(() => {
    if (leaveTypeData) {
    }
  }, [leaveTypeData]);

  const handleMonthClick = (idx: number) => {
    setSelectedMonth(idx);
    setIsMonthDropdownOpen(false);
  };

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });
    return map;
  }, [employees]);

  const handleViewAll = (type: string) => {
    setViewType(type.toLowerCase());
    setIsPendingView(false); // Reset to show all records
    setTableData(
      originalData.filter((record) => getRecordType(record) === type)
    );

    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handlePendingView = (type: string) => {
    setViewType(type.toLowerCase());
    setIsPendingView(true); // Set flag to show only pending
    setTableData(
      originalData.filter(
        (record) =>
          record.status === "Pending" && getRecordType(record) === type
      )
    );

    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const getRecordType = (record: any): string => {
    if ("permission_id" in record) return "permission";
    if ("Od_id" in record) return "od";
    if ("wfm_id" in record) return "wfh";
    if ("request_id" in record) return "leave";
    return "";
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let data: TableRecord[] = [];

        if (viewType === "permission") data = await getAllPermissions(empToken);
        else if (viewType === "od") data = await getAllOnDuties(empToken);
        else if (viewType === "wfh") data = await getAllWorkFromHome(empToken);
        else if (viewType === "leave") data = await getAllApplyLeave(empToken);

        setOriginalData(data);
        setTableData(
          isPendingView
            ? data.filter(
                (record) =>
                  record.status === "Pending" &&
                  getRecordType(record) === viewType
              )
            : data
        );
      } catch (err) {
        console.error(err);
        setTableData([]);
        setOriginalData([]);
      } finally {
        setLoading(false);
      }
    };

    if (viewType !== "default") {
      fetchData();
    }
  }, [viewType, empToken, isPendingView]);

  const handleApprove = async (record: any) => {
    try {
      if (viewType === "permission") {
        await updatePermissionStatus(
          {
            permission_id: record.permission_id,
            status: "Approved",
            notify: "",
          },
          empToken
        );
      } else if (viewType === "od") {
        if (!record?.Od_id) {
          console.error("Missing On-Duty ID");
          return;
        }
        await updateOnDutyStatus(
          { on_duty_id: record.Od_id, status: "Approved", notify: "" },
          empToken
        );
      } else if (viewType === "wfh") {
        await updateWorkFromHomeStatus(
          { wfm_id: record.wfm_id, status: "Approved", notify: "" },
          empToken
        );
      } else if (viewType === "leave") {
        await putApplyLeave(
          { request_id: record.request_id, status: "Approved", notify: "" },
          empToken
        );
      }

      // Update table data in-place using unique IDs
      setOriginalData((prevData) =>
        prevData.map((r) => {
          if (getRecordId(r) === getRecordId(record)) {
            return { ...r, status: "Approved" };
          }
          return r;
        })
      );

      setTableData((prevData: any[]) =>
        prevData.map((r) => {
          if (
            (viewType === "permission" &&
              r.permission_id === record.permission_id) ||
            (viewType === "od" && r.Od_id === record.Od_id) ||
            (viewType === "wfh" && r.wfm_id === record.wfm_id) ||
            (viewType === "leave" && r.request_id === record.request_id)
          ) {
            return { ...r, status: "Approved" };
          }
          return r;
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleRejectClick = (record: any) => {
    setSelectedRecord(record);
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.warning("Please enter a rejection reason");
      return;
    }
    if (!selectedRecord) return;
    try {
      if (viewType === "permission") {
        await updatePermissionStatus(
          {
            permission_id: selectedRecord.permission_id,
            status: "Rejected",
            reject_reason: rejectReason,
            notify: "",
          },
          empToken
        );
      } else if (viewType === "od") {
        await updateOnDutyStatus(
          {
            on_duty_id: selectedRecord.Od_id,
            status: "Rejected",
            reject_reason: rejectReason,
            notify: "",
          },
          empToken
        );
      } else if (viewType === "wfh") {
        await updateWorkFromHomeStatus(
          {
            wfm_id: selectedRecord.wfm_id,
            status: "Rejected",
            reject_reason: rejectReason,
            notify: "",
          },
          empToken
        );
      } else if (viewType === "leave") {
        await putApplyLeave(
          {
            request_id: selectedRecord.request_id,
            status: "Rejected",
            reject_reason: rejectReason,
            notify: "",
          },
          empToken
        );
      }

      // Clear the selected status for this specific record
      const recordId = getRecordId(selectedRecord);
      setSelectedStatuses((prev) => {
        const newStatuses = { ...prev };
        delete newStatuses[recordId];
        return newStatuses;
      });

      // Refresh only the table data for the current viewType
      const updatedData = await (async () => {
        if (viewType === "permission") return await getAllPermissions(empToken);
        else if (viewType === "od") return await getAllOnDuties(empToken);
        else if (viewType === "wfh") return await getAllWorkFromHome(empToken);
        else if (viewType === "leave") return await getAllApplyLeave(empToken);
        return originalData;
      })();
      setOriginalData(updatedData);
      setTableData(
        isPendingView
          ? updatedData.filter(
              (record) =>
                record.status === "Pending" &&
                getRecordType(record) === viewType
            )
          : updatedData
      );

      setRejectModalOpen(false);
      setRejectReason("");
      setSelectedRecord(null);
    } catch (error) {
      console.error(error);
    }
  };

  const getRecordId = (record: any): string | number => {
    if (viewType === "permission") return record.permission_id;
    if (viewType === "od") return record.Od_id;
    if (viewType === "wfh") return record.wfm_id;
    if (viewType === "leave") return record.request_id;
    return "";
  };

  const RequestCards = [
    {
      icon: <FontAwesomeIcon icon={faPersonWalkingLuggage} />,
      bg: "rgba(22, 163, 74, 0.1)",
      title: "Leave Apply",
      type: "leave",
      totalApplied: leaveStats.applied,
      approved: leaveStats.approved,
      rejected: leaveStats.rejected,
      pending: leaveStats.pending,
      color: "success",
    },
    {
      icon: <FontAwesomeIcon icon={faShieldHalved} />,
      bg: "rgba(219, 39, 119, 0.1)",
      title: "Permission Apply",
      type: "permission",
      totalApplied: permissionStats.applied,
      approved: permissionStats.approved,
      rejected: permissionStats.rejected,
      pending: permissionStats.pending,
      color: "primary",
    },
    {
      icon: <FontAwesomeIcon icon={faSuitcase} />,
      bg: "rgba(245, 158, 11, 0.1)",
      title: "OD Apply",
      type: "od",
      totalApplied: onDutyStats.applied,
      approved: onDutyStats.approved,
      rejected: onDutyStats.rejected,
      pending: onDutyStats.pending,
      color: "warning",
    },
    {
      icon: <FontAwesomeIcon icon={faLaptopHouse} />,
      bg: "rgba(59, 130, 246, 0.1)",
      title: "WFH Apply",
      type: "wfh",
      totalApplied: wfhStats.applied,
      approved: wfhStats.approved,
      rejected: wfhStats.rejected,
      pending: wfhStats.pending,
      color: "info",
    },
  ];

  const pendingRequestsData = [
    {
      icon: <FontAwesomeIcon icon={faPersonWalkingLuggage} />,
      title: "Leave Requests",
      count: leaveStats.pending,
      type: "leave",
      color: "#4ecdc4",
    },
    {
      icon: <FontAwesomeIcon icon={faShieldHalved} />,
      title: "Permission Requests",
      count: permissionStats.pending,
      type: "permission",
      color: "#ff6b6b",
    },
    {
      icon: <FontAwesomeIcon icon={faSuitcase} />,
      title: "OD Requests",
      count: onDutyStats.pending,
      type: "od",
      color: "#45b7d1",
    },
    {
      icon: <FontAwesomeIcon icon={faLaptopHouse} />,
      title: "WFH Requests",
      count: wfhStats.pending,
      type: "wfh",
      color: "#f39c12",
    },
  ];

  const formatTime = (time: string) => {
    return dayjs(`2020-01-01T${time}`).format("hh:mm A");
  };

  const dynamicColumns = () => {
    const commonColumns = [
      {
        title: "S.NO",
        dataIndex: "serial",
        // width: 30,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: "Employee",
        key: "employee_id",
        width: 250,
        render: (_: any, record: any) => {
          const employeeId = record.employee_id;
          const employee = employeeNameMap[employeeId || ""];
          const imageUrl =
            empImages[employeeId || ""] || "/assets/img/defaultavatar.png";

          if (!employeeId) {
            return <span className="text-muted">-</span>;
          }
          return (
            <div className="d-flex align-items-center">
              <div className="avatar avatar-md">
                <img
                  src={imageUrl}
                  className="img-fluid rounded-circle"
                  alt="avatar"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = "/assets/img/defaultavatar.png";
                  }}
                />
              </div>
              <div className="ms-2">
                <p className="text-dark mb-0">
                  <b>{employee?.name || employeeId}</b>
                </p>
                <span className="fs-12">
                  {employee?.role || "Unknown Role"}
                </span>
              </div>
            </div>
          );
        },
      },
    ];

    const leaveSpecificColumns = [
      {
        title: "Leave Type",
        dataIndex: "leave_type_id",
        render: (leaveTypeId: number) => {
          const leaveTypeName =
            leaveTypeMap[leaveTypeId] || "Unknown Leave Type";
          return (
            <span className="badge rounded-pill bg-soft-info">
              {leaveTypeName}
            </span>
          );
        },
      },
      {
        title: "Start / End Date",
        width: 150,
        render: (record: any) => (
          <div>
            <span className="badge bg-transparent-purple">
              {record.start_date || "-"}
            </span>
            <br />
            <span className="badge bg-pink-transparent">{record.end_date}</span>
          </div>
        ),
      },
    ];

    const noOfDaysColumn = {
      title: "No. of Days",
      render: (_: any, record: any) => {
        const { no_of_days, date_apply, session_type, start_date, end_date } =
          record;

        const badgeClassMap: Record<string, string> = {
          "Full Day": "badge bg-transparent-purple",
          "Half Day": "badge bg-info-transparent",
          Custom: "badge bg-pink-transparent",
        };

        const renderBadge = (label: string) => (
          <span className={badgeClassMap[label] || "badge bg-light"}>
            {label}
          </span>
        );

        if (no_of_days === "Full Day") {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Full Day")}
              </h6>
              <div>
                <strong>Applied Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else if (no_of_days === "Half Day") {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Half Day")}
              </h6>
              <div>
                <strong>Session:</strong> {session_type || "N/A"}
              </div>
              <div>
                <strong>Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Custom")}
              </h6>
              <div>
                <strong>Start Date:</strong> {start_date || "N/A"}
              </div>
              <div>
                <strong>End Date:</strong> {end_date || "N/A"}
              </div>
            </>
          );
        }
      },
    };

    const permissionSpecificColumns = [
      {
        title: "Date",
        width: 120,
        dataIndex: "date_apply",
      },
      {
        title: "Start / End Time",
        render: (record: PermissionRecord) => (
          <>
            <span className="badge bg-transparent-purple">
              {formatTime(record.start_time)}
            </span>
            <br />
            <span className="badge bg-pink-transparent">
              {formatTime(record.end_time)}
            </span>
          </>
        ),
        width: 150,
      },
      {
        title: "Duration",
        dataIndex: "duration_hours",
        render: (value: number) => {
          const mins = Math.round(value * 60);
          return `${mins} mins`;
        },
      },
    ];

    const reasonAndStatusColumns = [
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        render: (text: string) => (
          <Input.TextArea
            value={text}
            readOnly
            style={{
              height: "50px",
              overflowY: "auto",
              resize: "none",
            }}
          />
        ),
      },
      {
        title: "Status",
        key: "status",
        render: (record: any) => {
          const isEditable = record.status === "Pending";
          const recordId = getRecordId(record);
          const currentSelectedStatus = selectedStatuses[recordId];

          const handleStatusChange = (value: string) => {
            setSelectedStatuses((prev) => ({
              ...prev,
              [recordId]: value, // store chosen value per record
            }));

            if (value === "Approved") {
              handleApprove(record);
              // Clear the selected status after approval
              setTimeout(() => {
                setSelectedStatuses((prev) => {
                  const newStatuses = { ...prev };
                  delete newStatuses[recordId];
                  return newStatuses;
                });
              }, 100);
            } else if (value === "Rejected") {
              handleRejectClick(record);
            }
          };

          return (
            <div className="d-flex align-items-center gap-2">
              <Tag
                color={
                  record.status === "Approved"
                    ? "green"
                    : record.status === "Rejected"
                    ? "red"
                    : "gold"
                }
              >
                {record.status}
              </Tag>

              {isEditable && (
                <Select
                  key={`${recordId}-${record.status}`} // Better key to force re-render when status changes
                  size="small"
                  value={currentSelectedStatus || undefined} // Use undefined instead of empty string
                  onChange={handleStatusChange}
                  style={{ width: 120 }}
                  placeholder="Update Status"
                >
                  <Option value="Approved">Approve</Option>
                  <Option value="Rejected">Reject</Option>
                </Select>
              )}
            </div>
          );
        },
      },
    ];

    const middleColumns =
      viewType === "permission"
        ? permissionSpecificColumns
        : viewType === "od" || viewType === "wfh"
        ? [noOfDaysColumn]
        : viewType === "leave"
        ? leaveSpecificColumns
        : [];

    return [...commonColumns, ...middleColumns, ...reasonAndStatusColumns];
  };

  const donutChartOptions: ApexOptions = {
    labels: leaveTypeData?.labels || [],
    legend: { position: "bottom" },
    colors: [
      "#10b981",
      "#3b82f6",
      "#6b7280",
      "#f9a8d4",
      "#a3a3a3",
      "#ef4444",
      "#f59e0b",
    ],
  };

  const donutChartSeries = leaveTypeData?.values || [];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
          <div className="my-auto mb-3">
            <h2 className="mb-1">Leave Management</h2>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to={all_routes.adminDashboard}>
                  <i className="ti ti-smart-home" />
                </Link>
              </li>
              <li className="breadcrumb-item active">Leave Dashboard</li>
            </ol>
          </div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "month" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => {
                  setViewMode("month");
                  setSelectedDate(null);
                }}
              >
                Month
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "date" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setViewMode("date")}
              >
                Date
              </button>
            </div>
            {viewMode === "date" ? (
              <ReactDatePicker
                selected={selectedDate ? selectedDate.toDate() : null}
                onChange={(date) => setSelectedDate(dayjs(date))}
                customInput={
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center"
                    style={{ maxHeight: "34px", minWidth: "130px" }}
                  >
                    <i className="ti ti-calendar me-2" />
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : "Select Date"}
                  </button>
                }
                dateFormat="dd-MM-yyyy"
                popperPlacement="bottom-start"
              />
            ) : (
              <div className="position-relative" style={{ width: "160px" }}>
                <button
                  onClick={toggleMonthDropdown}
                  className="btn btn-outline-primary d-flex align-items-center justify-content-between w-100"
                  style={{ maxHeight: "34px" }}
                >
                  <i className="ti ti-calendar-month me-2" />
                  {monthNames[selectedMonth]} - {selectedYear}
                  <i className="ti ti-chevron-down ms-auto" />
                </button>

                {isMonthDropdownOpen && (
                  <div
                    className="dropdown-menu show p-3 shadow position-absolute bg-white"
                    style={{
                      top: "42px",
                      minWidth: "280px",
                      zIndex: 1000,
                      borderRadius: "8px",
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Select Month:
                      </label>
                      <div className="d-flex flex-wrap gap-1">
                        {monthNames.map((month, idx) => (
                          <button
                            key={month}
                            onClick={() => handleMonthClick(idx)}
                            className={`btn btn-sm ${
                              selectedMonth === idx
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            style={{ fontSize: "11px", padding: "4px 8px" }}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="form-label fw-semibold">Year:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(+e.target.value)}
                        min="2020"
                        max="2030"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <CollapseHeader />
            </div>
          </div>
        </div>
        <div className="card mb-4 position-relative">
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  <i className="ti ti-calendar me-1"></i>
                  Filtered by:{" "}
                  {selectedDate
                    ? selectedDate.format("DD-MM-YYYY")
                    : `${monthNames[selectedMonth]} ${selectedYear}`}
                </small>
              </div>
              <small className="text-muted">
                Total Leave Types: {RequestCards.length}
              </small>
            </div>
            <div
              className="d-flex flex-wrap"
              style={{
                gap: "1rem",
              }}
            >
              {RequestCards.map((widget, idx) => (
                <div
                  key={idx}
                  className="card flex-grow-1"
                  style={{
                    flex: "1 1 calc(25% - 1rem)", // 4 cards per row, adjusts automatically
                    minWidth: "220px", // allows shrinking for smaller screens
                  }}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{widget.title}</h6>
                      <button
                        type="button"
                        onClick={() => handleViewAll(widget.type)}
                        className="btn btn-link p-0 text-primary small fw-semibold"
                      >
                        View All
                      </button>
                    </div>
                    <div className="d-flex align-items-center mb-1">
                      <span
                        className={`bg-${widget.color} text-white rounded me-2`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      >
                        {widget.icon}
                      </span>
                      <span className="text-muted small">Total Applied</span>
                    </div>
                    <h3 className="mb-0">{widget.totalApplied}</h3>
                    <div className="d-flex flex-wrap column-gap-3 row-gap-1 mt-2">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-success rounded-circle me-1"
                          style={{ width: "8px", height: "8px" }}
                        ></div>
                        <small className="text-muted">
                          Approved: {widget.approved}
                        </small>
                      </div>

                      <div className="d-flex align-items-center">
                        <div
                          className="bg-danger rounded-circle me-1"
                          style={{ width: "8px", height: "8px" }}
                        ></div>
                        <small className="text-muted">
                          Rejected: {widget.rejected}
                        </small>
                      </div>

                      <div className="d-flex align-items-center">
                        <div
                          className="bg-warning rounded-circle me-1"
                          style={{ width: "8px", height: "8px" }}
                        ></div>
                        <small className="text-muted">
                          Pending: {widget.pending}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row my-4">
          <div className="col-lg-6">
            <div className="card rounded-lg shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 fw-semibold">Pending Requests</h5>
                    <small className="text-muted">
                      Current pending approvals
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body pt-2">
                <div
                  className="d-flex flex-column gap-2"
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    paddingRight: "8px",
                  }}
                >
                  {pendingRequestsData.map((item, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between p-3 border rounded bg-light"
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle me-3 d-flex align-items-center justify-content-center text-white"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: item.color,
                            fontSize: "16px",
                          }}
                        >
                          {item.icon}
                        </div>
                        <div>
                          <h6 className="mb-0 fw-semibold">{item.title}</h6>
                          <small className="text-muted">{item.type}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <span
                          className="badge rounded-pill px-3 py-2 text-white fw-semibold d-inline-flex align-items-center"
                          style={{ backgroundColor: item.color }}
                        >
                          {item.count}
                          <i
                            className="ti ti-arrow-right text-dark ms-2"
                            style={{ cursor: "pointer", fontSize: "1.0rem" }}
                            onClick={() => handlePendingView(item.type)}
                          ></i>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card rounded-lg shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0">
                <h5 className="mb-0">Leave Type</h5>
              </div>
              <div>
                {leaveTypeData ? (
                  <ReactApexChart
                    options={donutChartOptions}
                    series={donutChartSeries}
                    type="donut"
                    height={250}
                  />
                ) : (
                  <p className="text-center text-muted py-5">
                    Loading chart...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {viewType !== "default" && (
          <div className="card" ref={tableRef}>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div>
                <h5 className="mb-0">
                  {viewType === "leave"
                    ? "Leave Request List"
                    : viewType === "permission"
                    ? "Permission Request List"
                    : viewType === "od"
                    ? "OD Request List"
                    : viewType === "wfh"
                    ? "WFH Request List"
                    : "Requests"}
                </h5>
              </div>
              <div
                className="d-flex align-items-center ms-auto"
                style={{ minWidth: 250 }}
              >
                <Input.Search
                  allowClear
                  placeholder="Search..."
                  onChange={(e) => {
                    const searchValue = e.target.value.toLowerCase();

                    const filtered = originalData.filter((record) => {
                      const employee =
                        employeeNameMap[record.employee_id || ""];

                      const searchableFields: (string | undefined)[] = [
                        "reason" in record ? record.reason : undefined,
                        "status" in record ? record.status : undefined,
                        "date_apply" in record
                          ? (record as any).date_apply
                          : undefined,
                        "start_date" in record
                          ? (record as any).start_date
                          : undefined,
                        "end_date" in record
                          ? (record as any).end_date
                          : undefined,
                        "session_type" in record
                          ? (record as any).session_type
                          : undefined,
                        "no_of_days" in record
                          ? (record as any).no_of_days
                          : undefined,
                        "start_time" in record
                          ? (record as any).start_time
                          : undefined,
                        "end_time" in record
                          ? (record as any).end_time
                          : undefined,
                        "duration_hours" in record
                          ? String((record as any).duration_hours)
                          : undefined,
                        employee?.name,
                        employee?.role,
                        employee?.department,
                      ];

                      return searchableFields.some((field) =>
                        String(field || "")
                          .toLowerCase()
                          .includes(searchValue)
                      );
                    });

                    setTableData(filtered);
                  }}
                />
              </div>
            </div>
            <div className="card-body p-0">
              <div className="custom-datatable-filter table-responsive">
                <Table
                  rowKey={(record) => getRecordId(record)}
                  columns={dynamicColumns()}
                  dataSource={tableData}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 rounded-bottom">
        <p className="mb-0">2024 - 2025 © IATT.</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            IATT
          </Link>
        </p>
      </div>

      <Modal
        title="Reject Request"
        open={rejectModalOpen}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason("");
          setSelectedRecord(null);
        }}
        okText="Submit"
        cancelText="Cancel"
      >
        <p>Please provide a reason for rejection:</p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={4}
          placeholder="Enter reason..."
        />
      </Modal>
    </div>
  );
};

export default LeaveDashboard;
